/**
 * 後台 — 單一會員 360 view（#4 + #14 燕窩存摺管理）
 *
 * 顯示：基本資料、會籍、燕窩存摺（餘額 + 出入帳）、權益人、標籤、最近訂單、最近互動
 * 操作：存摺手動入帳 / 出帳（真金流接通前的過渡 UX）
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Pencil, Settings, Trash2 } from "lucide-react";
import {
  adminAddTag,
  adminAdjustSpent,
  adminDeleteMember,
  adminExtendMembership,
  adminGrantTimeless,
  adminListOnboardingSubmissions,
  adminListSpendAdjustments,
  adminListTimelessPlans,
  adminMergeMembers,
  adminRefreshLineProfile,
  adminRemoveTag,
  adminSetMemberStatus,
  adminSetMembershipDates,
  adminSetTier,
  adminUpdateMember,
  getConfig,
  getMember360,
  listMembers,
  type OnboardingSubmission,
  type SpendAdjustment,
  type TimelessPlan,
} from "../../api/admin";
import type { Member } from "../../api/members";
import {
  adminGetWallet,
  adminGetWalletTransactions,
  adminWalletCredit,
  adminWalletDebit,
  adminWalletDeleteTxn,
  WALLET_CATEGORY_NAMES,
  WALLET_CATEGORY_UNITS,
  WALLET_REASON_LABELS,
  type WalletBalance,
  type WalletCategory,
  type WalletTxn,
} from "../../api/wallet";
import MemberAvatar from "../../components/MemberAvatar";
import TierBadge, { TIER_LABEL, type Tier } from "../../components/TierBadge";
import { useUIStore } from "../../store/uiStore";
import { previewMembershipPeriod } from "../../utils/date";

// 會員狀態中文(後台不露英文 code)
const MEMBER_STATUS_LABEL: Record<string, string> = {
  pending: "待入會",
  active: "正常",
  suspended: "已停用",
  merged: "已合併",
};

// 會籍相關操作的錯誤碼 → 中文(後端回的 status 是英文 code,不直接丟給使用者看)
const MEMBERSHIP_ERROR_TEXT: Record<string, string> = {
  invalid_date: "日期格式不正確",
  end_before_start: "到期日不可早於起算日",
  member_not_found: "找不到這位會員",
  invalid_tier: "等級不正確",
  invalid_period_mode: "期間設定不正確",
  no_change: "等級與期間都沒有變更",
};

function tierLabel(code?: string): string {
  return TIER_LABEL[code as Tier] || code || "—";
}

const RESULT_LABEL: Record<string, string> = {
  ok: "首次入會成功",
  already_active: "重填(已入會)",
  phone_taken: "手機重複",
  invalid: "欄位缺失",
};

const RESULT_STYLE: Record<string, string> = {
  ok: "bg-emerald-100 text-emerald-700",
  already_active: "bg-sky-100 text-sky-700",
  phone_taken: "bg-amber-100 text-amber-700",
  invalid: "bg-gray-200 text-gray-600",
};

export default function MemberDetail() {
  const { uid = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [wallet, setWallet] = useState<WalletBalance | null>(null);
  const [walletTxns, setWalletTxns] = useState<WalletTxn[]>([]);
  const [walletModal, setWalletModal] = useState<"credit" | "debit" | null>(null);
  const [spendModal, setSpendModal] = useState(false);
  const [timelessModal, setTimelessModal] = useState(false);
  const [editMode, setEditMode] = useState<null | "profile" | "membership">(null);
  const [newTag, setNewTag] = useState("");
  const [submissions, setSubmissions] = useState<OnboardingSubmission[]>([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [refreshingProfile, setRefreshingProfile] = useState(false);

  async function refresh360() {
    try {
      const r = await getMember360(uid);
      setData(r);
    } catch {
      useUIStore.getState().showToast("載入失敗", "error");
    }
  }

  async function refreshWallet() {
    if (!uid) return;
    try {
      const [w, t] = await Promise.all([
        adminGetWallet(uid),
        adminGetWalletTransactions(uid, 50),
      ]);
      setWallet(w);
      setWalletTxns(t);
    } catch {
      // ignore — 載入失敗不擋 360 主要資料
    }
  }

  async function handleDeleteTxn(tx: WalletTxn) {
    const sign = tx.type === "credit" ? "+" : "−";
    const label = WALLET_REASON_LABELS[tx.reason] || tx.reason;
    const amount = tx.qty ?? tx.bottles ?? 0;   // 新制品類交易用 qty、舊制用 bottles
    const unit = tx.unit || "瓶";
    // 對應「真實交易」的入帳(訂單付款 / 送禮 / 推薦獎勵)— 刪存摺這筆不會動到訂單/贈禮本身,
    // 會造成存摺與訂單對不上 → 給更強警語 + 二次確認;admin 手動 / 認親合併維持單次確認
    const REAL_TXN = ["order_paid", "gift_sent", "gift_received", "referral_referrer", "referral_referee"];
    if (REAL_TXN.includes(tx.reason)) {
      const isOrder = tx.reason === "order_paid";
      const src = isOrder ? "訂單" : "贈禮";
      if (!confirm(
        `注意:「${label}」是對應一筆真實${isOrder ? "付款訂單" : "交易"}的入帳。\n\n` +
        `刪掉只移除存摺這一筆,${isOrder ? "訂單本身仍存在" : "原交易仍存在"} → 存摺與${src}會對不上帳。\n` +
        `正常應從「${src}」那邊處理,而不是刪存摺這筆。\n\n仍要強制刪除嗎?`
      )) return;
      if (!confirm(`再次確認:永久刪除「${label} ${sign}${amount} ${unit}」?此操作無法復原。`)) return;
    } else if (!confirm(`確定刪除這筆交易「${label} ${sign}${amount} ${unit}」?\n餘額會自動回正,此操作無法復原。`)) {
      return;
    }
    try {
      const r = await adminWalletDeleteTxn(uid, tx.txn_id);
      if (r.status === "ok") {
        useUIStore.getState().showToast(`已刪除,餘額回正為 ${r.balance_after} ${unit}`, "success");
        await refreshWallet();
      } else if (r.status === "would_go_negative") {
        useUIStore.getState().showToast("刪除會讓餘額變負(該筆瓶數已被花掉部分),請改用出帳調整", "error");
      } else {
        useUIStore.getState().showToast(`刪除未成功:${r.status}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("刪除失敗", "error");
    }
  }

  async function refreshSubmissions() {
    if (!uid) return;
    try {
      const r = await adminListOnboardingSubmissions(uid);
      setSubmissions(r.submissions);
    } catch {
      // ignore — debug 資料載入失敗不擋 360 主流
    }
  }

  useEffect(() => {
    (async () => {
      await refresh360();
      await Promise.all([refreshWallet(), refreshSubmissions()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // 重打 LINE profile API 更新暱稱 + 頭像(客戶換了頭像 / 暱稱,後台才跟得上)
  async function handleRefreshProfile() {
    if (refreshingProfile) return;
    setRefreshingProfile(true);
    try {
      const r = await adminRefreshLineProfile(uid);
      if (r.status === "ok") {
        useUIStore.getState().showToast("已更新暱稱與頭像", "success");
        await refresh360();
      } else if (r.status === "profile_unavailable") {
        useUIStore.getState().showToast("無法取得,對方可能已封鎖官方帳號", "error");
      } else {
        useUIStore.getState().showToast(MEMBERSHIP_ERROR_TEXT[r.status] || "更新未成功", "error");
      }
    } catch {
      useUIStore.getState().showToast("更新失敗", "error");
    } finally {
      setRefreshingProfile(false);
    }
  }

  async function handleAddTag() {
    const t = newTag.trim();
    if (!t) return;
    const r = await adminAddTag(uid, t);
    if (r.status === "ok") {
      setNewTag("");
      await refresh360();
    } else {
      useUIStore.getState().showToast(`加標籤未成功:${r.status}`, "error");
    }
  }

  async function handleRemoveTag(tag: string) {
    const r = await adminRemoveTag(uid, tag);
    if (r.status === "ok") {
      await refresh360();
    }
  }

  async function handleDelete(force = false) {
    if (!force && !confirm("確定永久刪除此會員?此操作無法復原。")) return;
    try {
      const r = await adminDeleteMember(uid, force);
      if (r.status === "ok") {
        useUIStore.getState().showToast("會員已刪除", "success");
        navigate("/admin/members");
      } else if (r.status === "has_history") {
        const yes = confirm(
          `此會員有 ${r.orders} 筆訂單、存摺 ${r.wallet_bottles} 瓶。\n` +
          `建議改用「合併」或停用。\n仍要強制刪除嗎?(訂單 / 贈禮等帳務記錄會保留)`,
        );
        if (yes) await handleDelete(true);
      } else {
        useUIStore.getState().showToast(`刪除未成功:${r.status}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("刪除失敗", "error");
    }
  }

  if (!data) return <div className="p-10 text-center text-muted">載入中…</div>;

  const m = data.member || {};
  const tier = m.tier || "general";

  return (
    <div className="min-h-screen bg-cream pb-10">
      <header className="bg-primary text-white px-6 py-5 flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="text-xl">‹</button>
        <MemberAvatar src={m.picture_url} name={m.name || m.display_name} size={48} />
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold">{m.name || m.display_name || uid}</h1>
            <TierBadge tier={tier} />
          </div>
          {/* 後台手動建的會員(manual_)沒有 LINE 帳號,沒東西可更新 */}
          {!uid.startsWith("manual_") && (
            <button
              onClick={handleRefreshProfile}
              disabled={refreshingProfile}
              className="mt-1 text-xs text-white/80 underline hover:text-white disabled:opacity-60"
            >
              {refreshingProfile ? "更新中…" : "更新 LINE 資料"}
            </button>
          )}
        </div>
      </header>

      <div className="p-6 grid lg:grid-cols-2 gap-5">
        <Section
          title="基本資料"
          action={
            <button
              onClick={() => setEditMode("profile")}
              className="text-xs text-primary hover:underline"
            >
              <Pencil size={12} className="inline -mt-0.5 mr-1" aria-hidden="true" />編輯
            </button>
          }
        >
          <Row label="LINE UID" value={m.line_user_id} />
          <Row label="姓名" value={m.name} />
          <Row label="顯示名" value={m.display_name} />
          <Row label="手機" value={`${m.phone || ""}${m.phone_verified ? " (已驗證)" : ""}`} />
          <Row label="Email" value={m.email} />
          <Row label="生日" value={m.birthday} />
          <Row
            label="地址"
            value={
              m.address
                ? [m.address.city, m.address.district, m.address.detail].filter(Boolean).join(" ")
                : "—"
            }
          />
          <Row label="推薦來源" value={referredByText(m.referred_by)} />
          <Row
            label="狀態"
            value={
              m.status === "suspended"
                ? <span className="text-coral">已停用</span>
                : (MEMBER_STATUS_LABEL[m.status] || m.status || "—")
            }
          />
          {/* 客戶封鎖官方帳號 ≠ 後台停用。2026-09-16 前兩者混用同一個 status,
              造成「封鎖後再加回來」的客戶被永久當成停用、下單送禮全被擋。 */}
          <Row
            label="官方帳號"
            value={
              m.is_following === false
                ? "已封鎖 / 移除好友"
                : m.is_following === true
                  ? "好友中"
                  : "—"
            }
          />
          <Row label="備註" value={m.admin_note ? <span className="whitespace-pre-wrap">{m.admin_note}</span> : "—"} />
        </Section>

        <Section
          title="會籍"
          action={
            <button
              onClick={() => setEditMode("membership")}
              className="text-xs text-primary hover:underline"
            >
              <Settings size={12} className="inline -mt-0.5 mr-1" aria-hidden="true" />設定
            </button>
          }
        >
          <Row label="會籍等級" value={TIER_LABEL[m.tier as keyof typeof TIER_LABEL] || m.tier} />
          <Row
            label="總消費"
            value={
              <span className="inline-flex items-center gap-2">
                NT$ {(m.total_spent || 0).toLocaleString()}
                <button onClick={() => setSpendModal(true)} className="text-xxs text-primary hover:underline">
                  調整
                </button>
              </span>
            }
          />
          <Row label="總訂單" value={String(m.total_orders || 0)} />
          <Row label="會籍起" value={fmt(m.membership_start)} />
          <Row label="會籍迄" value={fmt(m.membership_end)} />
          <Row label="最後互動" value={fmt(m.last_interaction_at)} />
          <div className="pt-2 mt-1 border-t border-line">
            <button onClick={() => setTimelessModal(true)} className="text-xs text-primary hover:underline">
              ＋ 灌入恆時薈入會配額(即飲 / 鮮燉方案)
            </button>
          </div>
        </Section>

        {/* #14 燕窩存摺 — 餘額 + 入帳/出帳 + 最近 5 筆明細 */}
        <Section title="燕窩存摺">
          {!wallet ? (
            <div className="text-muted text-sm">載入中…</div>
          ) : (
            <>
              {/* 品類餘額 — 即飲 / 鮮燉 / 留白禮盒 + 內部包材(禮盒/紙袋)各記各的,單位不互換。
                  後台全品類都顯示(要能從 0 入帳);會員端 internal 品類沒往來不顯示 */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {wallet.categories.map((c) => (
                  <div key={c.category} className="bg-cream rounded-lg p-3 text-center">
                    <div className="text-xxs text-muted">
                      {c.name}
                      {c.spec && <span className="opacity-70"> {c.spec}</span>}
                    </div>
                    <div className="mt-1">
                      <span className="text-xl font-bold text-primary">{c.balance.toLocaleString()}</span>
                      <span className="text-xxs text-muted ml-0.5">{c.unit}</span>
                    </div>
                    <div className="text-xxs text-muted mt-1">入{c.credited}·出{c.debited}</div>
                  </div>
                ))}
              </div>
              {/* 既有餘額(舊制單一瓶數,訂單路徑寫的)— >0 才顯示,待後續歸類 */}
              {wallet.balance_bottles > 0 && (
                <div className="flex justify-between items-center text-xs bg-amber-50 text-amber-800 rounded px-3 py-2 mb-3">
                  <span>既有餘額(舊制,待歸類)</span>
                  <span className="font-bold">{wallet.balance_bottles.toLocaleString()} 瓶</span>
                </div>
              )}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setWalletModal("credit")}
                  className="btn-primary text-xs px-3 py-1.5 flex-1"
                >
                  ＋ 入帳
                </button>
                <button
                  onClick={() => setWalletModal("debit")}
                  className="btn-secondary text-xs px-3 py-1.5 flex-1"
                >
                  − 出帳
                </button>
              </div>
              {walletTxns.length === 0 ? (
                <div className="text-muted text-sm">尚無明細</div>
              ) : (
                <div className="space-y-1">
                  {walletTxns.slice(0, 5).map((tx) => {
                    const amount = tx.qty ?? tx.bottles ?? 0;
                    const unit = tx.unit || "瓶";
                    const catName = tx.category ? WALLET_CATEGORY_NAMES[tx.category] || "" : "";
                    return (
                      <div
                        key={tx.txn_id}
                        className="flex items-start justify-between text-xs py-1.5 border-b border-line last:border-0"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-ink">
                            {WALLET_REASON_LABELS[tx.reason] || tx.reason}
                            {catName && <span className="text-muted ml-1">· {catName}</span>}
                            {tx.note && (
                              <span className="text-muted ml-1">· {tx.note.slice(0, 20)}</span>
                            )}
                          </div>
                          <div className="text-xxs text-muted mt-0.5">{fmt(tx.created_at)}</div>
                        </div>
                        <div className={`text-right shrink-0 ml-2 ${tx.type === "credit" ? "text-primary" : "text-coral"}`}>
                          <div className="font-bold">
                            {tx.type === "credit" ? "+" : "−"} {amount} {unit}
                          </div>
                          <div className="text-xxs text-muted">餘 {tx.balance_after} {unit}</div>
                        </div>
                        <button
                          onClick={() => handleDeleteTxn(tx)}
                          className="shrink-0 ml-2 mt-0.5 text-muted hover:text-coral leading-none"
                          title="刪除此交易(餘額自動回正)"
                          aria-label="刪除此交易"
                        >
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      </div>
                    );
                  })}
                  {walletTxns.length > 5 && (
                    <div className="text-xxs text-muted text-center pt-2">⋯ 共 {walletTxns.length} 筆</div>
                  )}
                </div>
              )}
            </>
          )}
        </Section>

        <Section title="權益人">
          {(m.beneficiaries || []).length === 0 && (
            <div className="text-muted text-sm">尚未新增</div>
          )}
          {(m.beneficiaries || []).map((b: any, i: number) => (
            <div key={i} className="py-2 border-b border-line last:border-0">
              <div className="font-medium">{b.name} <span className="text-muted text-sm">({b.relation})</span></div>
              <div className="text-sm text-muted">{b.phone}</div>
            </div>
          ))}
        </Section>

        <Section title="標籤">
          <div className="flex flex-wrap gap-2 mb-3">
            {(m.tags || []).length === 0 ? (
              <span className="text-muted text-sm">尚無標籤</span>
            ) : (
              (m.tags || []).map((t: string) => (
                <span
                  key={t}
                  className="bg-accent/10 text-accent text-xs px-2 py-1 rounded-full flex items-center gap-1"
                >
                  {t}
                  <button
                    onClick={() => handleRemoveTag(t)}
                    className="text-muted hover:text-coral"
                    title="移除"
                  >
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
          <div className="flex gap-2">
            <input
              className="input flex-1 text-xs"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
              placeholder="輸入標籤後按 Enter"
              maxLength={40}
            />
            <button
              onClick={handleAddTag}
              className="btn-secondary text-xs px-3"
              disabled={!newTag.trim()}
            >
              加入
            </button>
          </div>
        </Section>

        <Section title="最近 5 筆訂單">
          {(data.recent_orders || []).length === 0 && (
            <div className="text-muted text-sm">尚無訂單</div>
          )}
          {(data.recent_orders || []).map((o: any) => (
            <Row key={o.order_id} label={fmt(o.created_at)} value={`NT$ ${(o.total_amount || 0).toLocaleString()} · ${o.payment_status}`} />
          ))}
        </Section>

        <Section title="最近 10 筆互動">
          {(data.recent_interactions || []).length === 0 && (
            <div className="text-muted text-sm">尚無互動</div>
          )}
          {(data.recent_interactions || []).map((it: any, i: number) => (
            <div key={i} className="text-sm py-1.5 border-b border-line last:border-0">
              <div className="text-ink">{it.type} <span className="text-muted ml-2">{(it.content || "").slice(0, 40)}</span></div>
              <div className="text-xs text-muted">{fmt(it.timestamp)}</div>
            </div>
          ))}
        </Section>

        <Section title={`歷史填寫記錄(${submissions.length})`}>
          {submissions.length === 0 && (
            <div className="text-muted text-sm">尚無 onboarding 表單記錄</div>
          )}
          {submissions.length > 0 && (
            <div className="text-xs text-muted mb-2">
              對照客戶實際填寫內容(投訴「我打對的」時可調閱)。時間倒序,最多 50 筆。
            </div>
          )}
          {submissions.map((s) => (
            <details key={s.id} className="border-b border-line last:border-0 py-2">
              <summary className="cursor-pointer text-sm flex items-center gap-2">
                <span className="text-ink">{fmt(s.submitted_at)}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${RESULT_STYLE[s.result] || "bg-line text-muted"}`}>
                  {RESULT_LABEL[s.result] || s.result}
                </span>
                <span className="text-xs text-muted ml-auto truncate">{s.form.email || "(無 email)"}</span>
              </summary>
              <div className="mt-2 pl-2 space-y-1 text-xs text-ink">
                <Row label="姓名" value={s.form.name || "—"} />
                <Row label="手機" value={s.form.phone || "—"} />
                <Row label="Email" value={`${s.form.email || "—"} (len=${s.form.email_raw_len})`} />
                <Row label="生日" value={s.form.birthday || "—"} />
                <Row label="地址" value={
                  [s.form.address_city, s.form.address_district, s.form.address_detail].filter(Boolean).join(" ") || "—"
                } />
                <Row label="推薦碼" value={s.form.referral_code || "—"} />
                <Row label="同意條款" value={s.form.consent ? "已同意" : "未同意"} />
                <Row label="IP" value={s.audit.ip || "—"} />
                <Row label="User-Agent" value={<span className="break-all">{s.audit.user_agent || "—"}</span>} />
              </div>
            </details>
          ))}
        </Section>

        {/* 帳號整理 — 合併 / 刪除(危險操作) */}
        <Section title="帳號整理">
          <div className="space-y-3">
            <div>
              <button
                onClick={() => setMergeOpen(true)}
                className="btn-secondary text-sm w-full"
                disabled={m.status === "merged"}
              >
                ⇆ 合併其他帳號到此會員
              </button>
              <div className="text-xs text-muted mt-1">
                把重複帳號的訂單 / 存摺 / 互動併進這個會員,對方會被標記為已合併。
              </div>
            </div>
            <div className="border-t border-line pt-3">
              <button onClick={() => handleDelete(false)} className="text-sm text-coral underline">
                <Trash2 size={14} className="inline -mt-0.5 mr-1" aria-hidden="true" />永久刪除此會員
              </button>
              <div className="text-xs text-muted mt-1">
                有訂單 / 存摺餘額會先擋下。重複帳號建議用「合併」、暫時停權用「停用」。
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* 存摺入帳 / 出帳 modal(先選品類)*/}
      {walletModal && wallet && (
        <WalletAdjustModal
          mode={walletModal}
          uid={uid}
          categories={wallet.categories}
          onClose={() => setWalletModal(null)}
          onSuccess={async () => {
            setWalletModal(null);
            await refreshWallet();
          }}
        />
      )}

      {/* 調整消費金額 modal */}
      {spendModal && (
        <SpendAdjustModal
          uid={uid}
          current={m.total_spent || 0}
          onClose={() => setSpendModal(false)}
          onSuccess={async () => {
            setSpendModal(false);
            await refresh360();
          }}
        />
      )}

      {/* 恆時薈入會配額 modal */}
      {timelessModal && (
        <TimelessGrantModal
          uid={uid}
          onClose={() => setTimelessModal(false)}
          onSuccess={async () => {
            setTimelessModal(false);
            await refreshWallet();
          }}
        />
      )}

      {/* 編輯基本資料 modal */}
      {editMode === "profile" && (
        <ProfileEditModal
          uid={uid}
          member={m}
          onClose={() => setEditMode(null)}
          onSuccess={async () => {
            setEditMode(null);
            await refresh360();
          }}
        />
      )}

      {/* 會籍設定 modal */}
      {editMode === "membership" && (
        <MembershipEditModal
          uid={uid}
          member={m}
          onClose={() => setEditMode(null)}
          onSuccess={async () => {
            setEditMode(null);
            await refresh360();
          }}
        />
      )}

      {/* 合併帳號 modal */}
      {mergeOpen && (
        <MergeModal
          primaryUid={uid}
          primaryName={m.name || m.display_name || uid}
          onClose={() => setMergeOpen(false)}
          onSuccess={async () => {
            setMergeOpen(false);
            await refresh360();
            await refreshWallet();
          }}
        />
      )}
    </div>
  );
}


function MergeModal({
  primaryUid,
  primaryName,
  onClose,
  onSuccess,
}: {
  primaryUid: string;
  primaryName: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState<Member[]>([]);
  const [picked, setPicked] = useState<Member | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!search.trim()) {
      setCandidates([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await listMembers({ search: search.trim(), limit: 20 });
        setCandidates(
          r.members.filter(
            (mm) => mm.line_user_id !== primaryUid && (mm.status as string) !== "merged",
          ),
        );
      } catch {
        // ignore — 搜尋失敗不擋
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [search, primaryUid]);

  async function doMerge() {
    if (!picked) return;
    setBusy(true);
    try {
      const r = await adminMergeMembers(primaryUid, picked.line_user_id);
      if (r.status === "ok") {
        const mv = r.moved || {};
        useUIStore.getState().showToast(
          `已合併(訂單 ${mv.orders || 0}、互動 ${mv.interactions || 0}、瓶數 ${mv.bottles || 0})`,
          "success",
        );
        onSuccess();
      } else if (r.status === "already_merged") {
        useUIStore.getState().showToast("此帳號已經合併過了", "error");
      } else {
        useUIStore.getState().showToast(`合併未成功:${r.status}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("合併失敗", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold">合併其他帳號</h2>
        <div className="text-sm text-muted">
          保留「<span className="text-ink font-medium">{primaryName}</span>」,
          把搜尋選到的重複帳號併入。對方的訂單 / 存摺 / 互動會搬過來,並標記為已合併(無法復原)。
        </div>

        <div>
          <label className="label">搜尋要併掉的帳號(姓名 / 電話 / Email)</label>
          <input
            className="input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPicked(null);
            }}
            placeholder="輸入關鍵字搜尋"
            autoFocus
          />
        </div>

        {searching && <div className="text-xs text-muted">搜尋中…</div>}

        {!picked && candidates.length > 0 && (
          <div className="border border-line rounded-lg divide-y divide-line max-h-60 overflow-y-auto">
            {candidates.map((c) => (
              <button
                key={c.line_user_id}
                onClick={() => setPicked(c)}
                className="w-full text-left px-3 py-2 hover:bg-cream text-sm"
              >
                <div className="font-medium">{c.name || c.display_name || "(無名)"}</div>
                <div className="text-xs text-muted">
                  {c.phone || "無電話"} · {TIER_LABEL[c.tier as keyof typeof TIER_LABEL] || c.tier || "—"}
                  {" · "}訂單 {c.total_orders || 0} · {(c.status as string) === "suspended" ? "已停用" : c.status}
                </div>
                <div className="text-xxs text-muted font-mono truncate">{c.line_user_id}</div>
              </button>
            ))}
          </div>
        )}

        {!picked && !searching && search.trim() && candidates.length === 0 && (
          <div className="text-sm text-muted">找不到符合的帳號</div>
        )}

        {picked && (
          <div className="border border-coral/40 bg-coral/5 rounded-lg p-3 space-y-2">
            <div className="text-sm font-medium text-coral">將併入並標記合併的帳號</div>
            <div className="text-sm">
              <div className="font-medium">{picked.name || picked.display_name || "(無名)"}</div>
              <div className="text-xs text-muted">
                {picked.phone || "無電話"} · 訂單 {picked.total_orders || 0}
              </div>
              <div className="text-xxs text-muted font-mono truncate">{picked.line_user_id}</div>
            </div>
            <div className="text-xs text-muted">
              併入後:訂單 / 贈禮 / 互動 / 存摺餘額移到「{primaryName}」,消費與訂單數相加,標籤與權益人聯集。
            </div>
            <button onClick={() => setPicked(null)} className="text-xs text-muted underline">
              重新選擇
            </button>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={busy}>
            取消
          </button>
          <button onClick={doMerge} className="btn-primary flex-1" disabled={!picked || busy}>
            {busy ? "合併中…" : "確認合併"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ProfileEditModal({
  uid,
  member,
  onClose,
  onSuccess,
}: {
  uid: string;
  member: any;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState(member.name || "");
  const [phone, setPhone] = useState(member.phone || "");
  const [email, setEmail] = useState(member.email || "");
  const [birthday, setBirthday] = useState(member.birthday || "");
  const [city, setCity] = useState(member.address?.city || "");
  const [district, setDistrict] = useState(member.address?.district || "");
  const [detail, setDetail] = useState(member.address?.detail || "");
  const [referralCode, setReferralCode] = useState(member.referred_by?.code || "");
  const [adminNote, setAdminNote] = useState(member.admin_note || "");
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const r = await adminUpdateMember(uid, {
        name,
        phone,
        email,
        birthday,
        address: { city, district, detail },
        referred_by_code: referralCode.trim(),
        admin_note: adminNote,
      });
      if (r.status === "ok" || r.status === "no_change") {
        useUIStore.getState().showToast("已更新", "success");
        onSuccess();
      } else {
        useUIStore.getState().showToast(`更新未成功:${r.status}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("連線異常", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold">編輯基本資料</h2>

        <div>
          <label className="label">姓名</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="label">手機</label>
          <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="label">生日</label>
          <input className="input" type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
        </div>
        <div>
          <label className="label">寄送地址</label>
          <div className="grid grid-cols-2 gap-2">
            <input className="input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="縣市" />
            <input className="input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="區域" />
            <input className="input col-span-2" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="詳細地址" />
          </div>
        </div>
        <div>
          <label className="label">推薦碼(業績分潤歸因)</label>
          <input
            className="input uppercase"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            placeholder="顧問 / 推薦人推薦碼,留空清除"
            maxLength={20}
          />
          <p className="text-xs text-muted mt-1">對應「顧問碼」管理頁的碼,用於計算這位客戶的推薦業績歸屬。</p>
        </div>
        <div>
          <label className="label">管理者備註</label>
          <textarea
            className="input resize-none"
            rows={3}
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="內部備註,客戶前台看不到"
            maxLength={2000}
          />
          <p className="text-xs text-muted mt-1">只給後台看,客戶端不會顯示。</p>
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={busy}>取消</button>
          <button onClick={save} className="btn-primary flex-1" disabled={busy}>
            {busy ? "儲存中…" : "儲存"}
          </button>
        </div>
      </div>
    </div>
  );
}


function MembershipEditModal({
  uid,
  member,
  onClose,
  onSuccess,
}: {
  uid: string;
  member: any;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [tier, setTier] = useState<string>(member.tier || "general");
  // 付費等級的期間處理:auto = 依設定自動帶入(預設,客戶 2026-10-06 要的);keep = 只改等級
  const [periodMode, setPeriodMode] = useState<"auto" | "keep">("auto");
  // 設定檔的期間規則(app_config.membership_period);讀不到就用預設,預覽文字與後端預設一致
  const [periodCfg, setPeriodCfg] = useState<{ start_mode: "next_month" | "today"; months: number }>({
    start_mode: "next_month",
    months: 12,
  });
  const [extraDays, setExtraDays] = useState("");
  const [endDate, setEndDate] = useState(member.membership_end ? String(member.membership_end).slice(0, 10) : "");
  const [startDate, setStartDate] = useState("");
  const [busy, setBusy] = useState(false);
  const isSuspended = member.status === "suspended";

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getConfig();
        const mp = cfg?.membership_period || {};
        const months = Number(mp.months);
        setPeriodCfg({
          start_mode: mp.start_mode === "today" ? "today" : "next_month",
          months: Number.isInteger(months) && months >= 1 ? months : 12,
        });
      } catch {
        // 設定讀不到不擋操作 — 預覽以預設值顯示,實際寫入以後端為準
      }
    })();
  }, []);

  const isPaid = tier !== "general";
  const preview = previewMembershipPeriod(periodCfg.start_mode, periodCfg.months);
  const startModeText = periodCfg.start_mode === "today" ? "當日" : "次月一日";
  const curStart = member.membership_start ? String(member.membership_start).slice(0, 10) : "";
  const curEnd = member.membership_end ? String(member.membership_end).slice(0, 10) : "";

  async function applyTier() {
    const sameTier = tier === member.tier;
    // 同等級:只有「付費等級 + 自動帶入」才有事可做(重設期間),其他都是沒變更
    if (sameTier && !(isPaid && periodMode === "auto")) {
      useUIStore.getState().showToast("等級未變更", "error");
      return;
    }
    if (sameTier && !confirm(`此會員目前已是「${tierLabel(tier)}」。\n要依設定把會籍期間重設為 ${preview.start} ~ ${preview.end} 嗎?`)) {
      return;
    }
    setBusy(true);
    try {
      const r = await adminSetTier(uid, tier, undefined, { period_mode: isPaid ? periodMode : "keep" });
      const s = String(r.membership_start || "").slice(0, 10);
      const e = String(r.membership_end || "").slice(0, 10);
      if (r.status === "ok") {
        const periodText = isPaid && periodMode === "auto" && s && e ? `;會籍 ${s} ~ ${e}` : "";
        useUIStore.getState().showToast(`等級變更:${tierLabel(r.from)} → ${tierLabel(r.to)}${periodText}`, "success");
        onSuccess();
      } else if (r.status === "period_only") {
        useUIStore.getState().showToast(`已重設會籍期間:${s} ~ ${e}`, "success");
        onSuccess();
      } else {
        useUIStore.getState().showToast(`變更未成功:${MEMBERSHIP_ERROR_TEXT[r.status] || "請稍後再試"}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("變更失敗", "error");
    } finally {
      setBusy(false);
    }
  }

  async function applyDates() {
    if (!endDate) {
      useUIStore.getState().showToast("請選擇到期日", "error");
      return;
    }
    if (startDate && endDate < startDate) {
      useUIStore.getState().showToast(MEMBERSHIP_ERROR_TEXT.end_before_start, "error");
      return;
    }
    setBusy(true);
    try {
      const r = await adminSetMembershipDates(uid, {
        membership_end: endDate,
        ...(startDate ? { membership_start: startDate } : {}),
      });
      if (r.status === "ok") {
        useUIStore.getState().showToast(`已指定會籍到期日:${String(r.membership_end || endDate).slice(0, 10)}`, "success");
        onSuccess();
      } else {
        useUIStore.getState().showToast(`設定未成功:${MEMBERSHIP_ERROR_TEXT[r.status] || "請稍後再試"}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("設定失敗", "error");
    } finally {
      setBusy(false);
    }
  }

  async function applyExtend() {
    const n = parseInt(extraDays, 10);
    if (isNaN(n) || n <= 0) {
      useUIStore.getState().showToast("請輸入正整數天數", "error");
      return;
    }
    setBusy(true);
    try {
      const r = await adminExtendMembership(uid, n);
      if (r.status === "ok") {
        useUIStore.getState().showToast(`已延長 ${n} 天`, "success");
        onSuccess();
      } else {
        useUIStore.getState().showToast(`延長未成功:${r.status}`, "error");
      }
    } finally {
      setBusy(false);
    }
  }

  async function toggleStatus() {
    const newStatus = isSuspended ? "active" : "suspended";
    const confirmMsg = isSuspended
      ? "重新啟用此會員?"
      : "停用此會員?(資料保留,但無法下單 / 送禮 / 領禮物,需由後台手動恢復)";
    if (!confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const r = await adminSetMemberStatus(uid, newStatus as any);
      if (r.status === "ok") {
        useUIStore.getState().showToast(isSuspended ? "已重新啟用" : "已停用", "success");
        onSuccess();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold">會籍設定</h2>

        <div className="border border-line rounded-lg p-3 space-y-2">
          <div className="text-sm font-medium">變更會籍等級</div>
          <select className="input" value={tier} onChange={(e) => setTier(e.target.value)}>
            {Object.entries(TIER_LABEL).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>

          {isPaid ? (
            <div className="space-y-1.5 pt-1">
              <div className="text-xs text-muted">會籍期間</div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="period_mode"
                  className="mt-1"
                  checked={periodMode === "auto"}
                  onChange={() => setPeriodMode("auto")}
                />
                <span>依設定自動帶入({startModeText}起 {periodCfg.months} 個月)</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="period_mode"
                  className="mt-1"
                  checked={periodMode === "keep"}
                  onChange={() => setPeriodMode("keep")}
                />
                <span>保留現有期間{curStart && curEnd ? `(${curStart} ~ ${curEnd})` : ""}</span>
              </label>
              {periodMode === "auto" && (
                <div className="text-xs text-primary bg-cream rounded px-2 py-1.5">
                  將設定為 {preview.start} ~ {preview.end}
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-muted">改為{tierLabel("general")}不會變動會籍期間。</div>
          )}

          <button onClick={applyTier} className="btn-primary w-full text-sm" disabled={busy}>
            套用等級變更
          </button>
        </div>

        <div className="border border-line rounded-lg p-3 space-y-2">
          <div className="text-sm font-medium">指定到期日</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs text-muted">
              起算日(選填)
              <input
                className="input mt-1"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label className="block text-xs text-muted">
              到期日(必填)
              <input
                className="input mt-1"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>
          <div className="text-xs text-muted">到期日當天整天仍有效;起算日不填就維持原本的起算日。</div>
          <button onClick={applyDates} className="btn-primary w-full text-sm" disabled={busy || !endDate}>
            套用
          </button>
        </div>

        <div className="border border-line rounded-lg p-3 space-y-2">
          <div className="text-sm font-medium">延長會籍</div>
          <input
            className="input"
            type="number"
            min="1"
            value={extraDays}
            onChange={(e) => setExtraDays(e.target.value)}
            placeholder="延長天數(如 365)"
          />
          <div className="text-xs text-muted">若已過期則從今天起算</div>
          <button onClick={applyExtend} className="btn-primary w-full text-sm" disabled={busy}>
            套用延長
          </button>
        </div>

        <div className="border border-coral/30 bg-coral/5 rounded-lg p-3 space-y-2">
          <div className="text-sm font-medium text-coral">
            {isSuspended ? "重新啟用會員" : "停用會員"}
          </div>
          <div className="text-xs text-muted">
            {isSuspended ? "停用中,點下方按鈕重新啟用。" : "停用後該會員不會被列入推播 / 互動,但資料保留。"}
          </div>
          <button onClick={toggleStatus} className="text-sm text-coral underline" disabled={busy}>
            {isSuspended ? "重新啟用" : "停用此會員"}
          </button>
        </div>

        <button onClick={onClose} className="btn-secondary w-full" disabled={busy}>關閉</button>
      </div>
    </div>
  );
}


function WalletAdjustModal({
  mode,
  uid,
  categories,
  onClose,
  onSuccess,
}: {
  mode: "credit" | "debit";
  uid: string;
  categories: WalletCategory[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [category, setCategory] = useState<string>(categories[0]?.category || "drink");
  const [qty, setQty] = useState<string>("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const cat = categories.find((c) => c.category === category) || categories[0];
  const unit = cat?.unit || "瓶";
  const curBalance = cat?.balance ?? 0;

  async function submit() {
    const n = parseInt(qty, 10);
    if (isNaN(n) || n <= 0) {
      useUIStore.getState().showToast(`請輸入有效${unit}數`, "error");
      return;
    }
    if (mode === "debit" && n > curBalance) {
      useUIStore.getState().showToast(`餘額不足(剩 ${curBalance} ${unit})`, "error");
      return;
    }
    setBusy(true);
    try {
      const r = mode === "credit"
        ? await adminWalletCredit(uid, category, n, note.trim())
        : await adminWalletDebit(uid, category, n, note.trim());
      if (r.status === "ok") {
        useUIStore.getState().showToast(
          `${mode === "credit" ? "已入帳" : "已出帳"} ${cat?.name ?? ""} ${n} ${unit}`,
          "success",
        );
        onSuccess();
      } else if (r.status === "insufficient_balance") {
        // insufficient_balance 只會從 debit 路徑回傳 — debit response type 含 balance,
        // 但 ternary 推 union 後 TS 拿不到,顯式 cast 為 debit return shape
        const debitR = r as { balance?: number };
        useUIStore.getState().showToast(`餘額不足(剩 ${debitR.balance ?? "?"} ${unit})`, "error");
      } else {
        useUIStore.getState().showToast(`操作失敗:${r.status}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("連線異常,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  const title = mode === "credit" ? "存摺入帳" : "存摺出帳";
  const hint = mode === "credit"
    ? "先選品類再入帳;活動限定優惠也走這裡。即飲 / 鮮燉以「瓶」、留白禮盒以「組」、禮盒 / 精美紙袋以「個」計。禮盒與紙袋是送禮包材(不對客販售),大量購買時可手動贈送入帳,會員存摺看得到。"
    : "先選品類再扣帳(實際出貨 / 核銷 / 調整)。";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
        <h2 className="text-lg font-bold">{title}</h2>
        <div className="text-xs text-muted">{hint}</div>

        {/* 品類選擇 */}
        <div>
          <label className="label">品類 *</label>
          <div className="grid grid-cols-3 gap-2">
            {categories.map((c) => (
              <button
                key={c.category}
                type="button"
                onClick={() => setCategory(c.category)}
                className={`text-xs px-2 py-2 rounded-lg border ${
                  category === c.category
                    ? "border-primary bg-primary/10 text-primary font-bold"
                    : "border-line text-muted"
                }`}
              >
                {c.name}
                <div className="text-xxs opacity-70 mt-0.5">{c.unit}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-cream rounded-lg p-3 text-xs text-muted">
          {cat?.name} 目前餘額:
          <span className="text-ink font-bold ml-1">{curBalance.toLocaleString()} {unit}</span>
        </div>

        <div>
          <label className="label">{unit}數 *</label>
          <input
            className="input"
            type="number"
            min="1"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder={`輸入${unit}數(整數)`}
          />
        </div>

        <div>
          <label className="label">備註(選填,會員存摺看得到)</label>
          <textarea
            className="input resize-none"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="例:中秋活動限定 +12 瓶 / 訂單 #abc1234 出貨核銷"
          />
          {/* 這段會原樣出現在會員存摺明細(Wallet.tsx 逐筆顯示 note)— 2026-10-06 Sam 定:保留顯示,這裡講清楚 */}
          <p className="text-xs text-muted mt-1">
            會原樣顯示在會員的存摺明細,請寫給會員看的說明;只給內部看的紀錄,請寫在會員資料的「管理者備註」。
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={busy}>
            取消
          </button>
          <button onClick={submit} className="btn-primary flex-1" disabled={busy}>
            {busy ? "處理中…" : mode === "credit" ? "確認入帳" : "確認出帳"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SpendAdjustModal({
  uid,
  current,
  onClose,
  onSuccess,
}: {
  uid: string;
  current: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [amount, setAmount] = useState<string>("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<SpendAdjustment[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminListSpendAdjustments(uid);
        setHistory(r.adjustments);
      } catch {
        // 歷史載入失敗不擋調整
      }
    })();
  }, [uid]);

  async function submit() {
    const n = parseInt(amount, 10);
    if (isNaN(n) || n === 0) {
      useUIStore.getState().showToast("請輸入調整金額(可負數扣回)", "error");
      return;
    }
    setBusy(true);
    try {
      const r = await adminAdjustSpent(uid, n, note.trim());
      if (r.status === "ok") {
        useUIStore.getState().showToast(`消費金額已調整為 NT$ ${(r.after ?? 0).toLocaleString()}`, "success");
        onSuccess();
      } else {
        useUIStore.getState().showToast(`調整失敗:${r.status}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("連線異常,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  const parsed = parseInt(amount, 10);
  const preview = amount && !isNaN(parsed) ? Math.max(0, current + parsed) : null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
        <h2 className="text-lg font-bold">調整消費金額</h2>
        <div className="text-xs text-muted">
          手動加減累計消費(可負數扣回)。自燉乾燕盞等不進存摺、但要算業績的品項走這裡補。會留調整記錄。
        </div>

        <div className="bg-cream rounded-lg p-3 text-xs text-muted">
          目前累計消費:<span className="text-ink font-bold ml-1">NT$ {current.toLocaleString()}</span>
        </div>

        <div>
          <label className="label">調整金額 *(正數增加,負數扣回)</label>
          <input
            className="input"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="例:21384 或 -5000"
          />
          {preview !== null && (
            <div className="text-xs text-muted mt-1">調整後:NT$ {preview.toLocaleString()}</div>
          )}
        </div>

        <div>
          <label className="label">備註(選填)</label>
          <textarea
            className="input resize-none"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="例:自燉久拾燕盞 108g 線下成交補登"
          />
        </div>

        {/* 消費調整歷史(audit)— 誰、改多少、前後值、備註 */}
        {history.length > 0 && (
          <div className="border-t border-line pt-3">
            <div className="text-xs text-muted font-medium mb-2">最近調整記錄</div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="text-xs py-1 border-b border-line last:border-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={h.amount >= 0 ? "text-primary font-medium" : "text-coral font-medium"}>
                      {h.amount >= 0 ? "+" : ""}{h.amount.toLocaleString()}
                    </span>
                    <span className="text-muted text-xxs">
                      {h.before.toLocaleString()} → {h.after.toLocaleString()}
                    </span>
                  </div>
                  {h.note && <div className="text-muted text-xxs break-words mt-0.5">{h.note}</div>}
                  <div className="text-muted text-xxs mt-0.5">
                    {h.operator} · {String(h.created_at).slice(0, 16).replace("T", " ")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={busy}>取消</button>
          <button onClick={submit} className="btn-primary flex-1" disabled={busy}>
            {busy ? "處理中…" : "確認調整"}
          </button>
        </div>
      </div>
    </div>
  );
}


function grantsLabel(p?: TimelessPlan): string {
  if (!p) return "";
  return p.grants
    .map((g) => `${WALLET_CATEGORY_NAMES[g.category] || g.category} ${g.qty} ${WALLET_CATEGORY_UNITS[g.category] || ""}`)
    .join(" + ");
}


function TimelessGrantModal({
  uid,
  onClose,
  onSuccess,
}: {
  uid: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [plans, setPlans] = useState<TimelessPlan[]>([]);
  const [plan, setPlan] = useState<string>("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminListTimelessPlans();
        setPlans(r.plans);
        if (r.plans[0]) setPlan(r.plans[0].key);
      } catch {
        useUIStore.getState().showToast("載入方案失敗", "error");
      }
    })();
  }, []);

  const selected = plans.find((p) => p.key === plan);

  async function submit() {
    if (!plan || !selected) return;
    if (!confirm(`確定灌入「${selected.name}」入會配額到此會員存摺?\n\n${grantsLabel(selected)}\n\n(會直接入帳;灌錯可在存摺刪掉那幾筆回退)`)) return;
    setBusy(true);
    try {
      const r = await adminGrantTimeless(uid, plan);
      if (r.status === "ok" || r.status === "partial") {
        useUIStore.getState().showToast(`已灌入 ${r.plan_name ?? selected.name} 配額`, "success");
        onSuccess();
      } else {
        useUIStore.getState().showToast(`灌入失敗:${r.status}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("連線異常,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
        <h2 className="text-lg font-bold">恆時薈入會配額</h2>
        <div className="text-xs text-muted">
          選一個方案(二選一),系統把對應瓶 / 組一次灌進此會員的三品類存摺。
        </div>

        <div className="space-y-2">
          {plans.length === 0 ? (
            <div className="text-muted text-sm">載入方案中…</div>
          ) : (
            plans.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPlan(p.key)}
                className={`w-full text-left px-3 py-3 rounded-lg border ${
                  plan === p.key ? "border-primary bg-primary/10" : "border-line"
                }`}
              >
                <div className="font-bold text-sm text-ink">{p.name}</div>
                <div className="text-xs text-muted mt-0.5">{grantsLabel(p)}</div>
              </button>
            ))
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={busy}>取消</button>
          <button onClick={submit} className="btn-primary flex-1" disabled={busy || !plan}>
            {busy ? "處理中…" : "確認灌入"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, action, children }: { title: string; action?: any; children: any }) {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-muted">{title}</div>
        {action}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-muted">{label}</span>
      <span className="text-ink text-right">{value || "—"}</span>
    </div>
  );
}

function fmt(ts: any): string {
  if (!ts) return "—";
  return String(ts).slice(0, 19).replace("T", " ");
}

// 推薦來源顯示:「王顧問 ABC123」;查無對應碼標(查無此碼)供人工對帳
function referredByText(rb: any): string {
  if (!rb || !rb.code) return "—";
  const label = rb.label ? `${rb.label} ` : "";
  const flag = rb.resolved ? "" : "(查無此碼)";
  return `${label}${rb.code}${flag}`;
}
